/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import EnforceIcon from "@/src/assets/icons/contract/toggleable/IconEnforce";
import AdvisoryIcon from "@/src/assets/icons/contract/toggleable/IconQuestionmarkBubble";
import Person from "@/src/components/Form/Common/Person/Person";
import _includes from "lodash/includes";
import Address from "@/src/components/Form/Common/Address/Address";
import PersonIcon from "@/src/assets/icons/provision/toggleable/IconPerson";
import FamilyIcon from "@/src/assets/icons/generic/toggleable/IconFamilyDetailed";
import BaseEnforceClaim from "./BaseEnforceClaim";

import { isPhoneNumber, minStringLength, requiredValidator } from "@/src/services/validationRules";
import { useForm } from "@/src/provider/Form/FormProvider";
import Group from "@/src/components/Wizard/Group";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase, CaseCategorySelectionMode } from "@/src/types/Entities/AbstractCase";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";
import CoinsuredClient from "@/src/components/Form/AbstractLitigationCase/CoinsuredClient";

type BaseProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  hideCaseCategories?: boolean;
  alternativeHeadline?: string;
};

export default function Base({ submitProps, product, hideCaseCategories = false, alternativeHeadline }: BaseProps) {
  const { values, handleSubmit, handleChange } = useForm();
  const shouldHideCaseCategories = hideCaseCategories || product.caseCategorySelectionMode === CaseCategorySelectionMode.FIXED;

  useEffect(() => {
    if (values.legalRepresentativeType === "me") {
      handleChange({
        target: {
          name: "clientLegalRepresentative.gender",
          value: values.customer.person.gender,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.givenName",
          value: values.customer.person.givenName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.familyName",
          value: values.customer.person.familyName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.streetAddress",
          value: values.customer.person.residenceAddress.streetAddress,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.postalCode",
          value: values.customer.person.residenceAddress.postalCode,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.addressLocality",
          value: values.customer.person.residenceAddress.addressLocality,
          type: "text",
        },
      });
      return;
    }
    handleChange({
      target: {
        name: "clientLegalRepresentative.gender",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.givenName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.familyName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.streetAddress",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.postalCode",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.addressLocality",
        value: "",
        type: "text",
      },
    });
  }, [values.legalRepresentativeType]);

  useEffect(() => {
    if (values.affectedPartyType !== "customer" || values.howManyClients === "oneClient") {
      handleChange({
        target: {
          name: "additionalClients",
          value: [],
        },
      });
      return;
    }
    if (values.howManyClients === "twoClients" && !values.additionalClients?.length) {
      handleChange({
        target: {
          name: "additionalClients",
          value: product.additionalClients?.length
            ? product.additionalClients
            : [
                {
                  givenName: "",
                  familyName: "",
                  gender: "",
                },
              ],
        },
      });
    }
  }, [values.howManyClients, values.affectedPartyType]);

  const getCaseCategoryOptions = () => {
    let options = [
      {
        labelText: "Ich möchte eine Forderung durchsetzen",
        labelIcon: <EnforceIcon />,
        labelIconActive: <EnforceIcon active />,
        value: "enforceClaim",
        optionHintText:
          "Ich möchte etwas mit rechtlicher Unterstützung durchsetzen lassen. Beispiele: Ich wurde von einem Hund gebissen. | Mein Nachbar macht ständig Lärm. | Jemand veröffentlicht Unwahrheiten über mich.",
      },
    ];
    if (product.categoryChange === null || product.categoryChange.previousCaseCategory !== "advisory") {
      options = [
        {
          labelText: "Ich benötige zunächst nur eine Erstberatung",
          labelIcon: <AdvisoryIcon />,
          labelIconActive: <AdvisoryIcon active />,
          value: "advisory",
          optionHintText:
            "Ich möchte nichts weiter veranlassen, sondern habe nur eine kurze Frage im Bereich Zivilrecht und benötige hierfür eine Erstberatung.",
        },
        ...options,
      ];
    }
    return options;
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ihr Anliegen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>{alternativeHeadline !== null ? alternativeHeadline : "Allgemeines zu Ihrem Anliegen"}</strong>
              </FormLegend>
            </Grid>
            {!shouldHideCaseCategories && (
              <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                <Group
                  type="radio"
                  name="caseCategory"
                  question="Wie können wir Ihnen weiterhelfen?"
                  options={getCaseCategoryOptions()}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            )}

            {values.caseCategory && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>
                    {values.caseCategory === "advisory"
                      ? "Bitte schildern Sie hier nur kurz in zwei bis drei Sätzen, was passiert ist und was Sie erreichen möchten. Detaillierte Angaben, wie Namen oder Adressen sind für eine Erstberatung zunächst nicht zwingend notwendig."
                      : "Bitte schildern Sie zunächst nur kurz in zwei bis drei Sätzen, was passiert ist und was Sie erreichen möchten. Detaillierte Angaben, wie Namen, Adressen oder weiterführende Informationen zum Sachverhalt, müssen Sie hier noch nicht machen. Diese können Sie in einem späteren Schritt ergänzen."}
                  </FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="initialClientDescriptionAndObjectives"
                    label="Beschreibung und Zielsetzung"
                    placeholder="Beispiel: Mein Nachbar betreibt regelmäßig sehr laute Feiern bis spät in die Nacht. Trotz mehrfacher Gespräche hat sich daran nichts geändert. Ich möchte, dass diese nächtliche Ruhestörung künftig unterlassen wird."
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      maxLength: 500,
                    }}
                    multiline
                    rows={8}
                    validators={[requiredValidator("Bitte beschreiben Sie was passiert ist und was Sie erreichen möchten.")]}
                    helperText={
                      "[" +
                      (values.initialClientDescriptionAndObjectives?.length ?? 0) +
                      "/500]" +
                      ((values.initialClientDescriptionAndObjectives?.length ?? 0) < 50 ? " Bitte geben Sie mindestens 50 Buchstaben ein" : "")
                    }
                  />
                </Grid>

                <Person path="customer.person" headline={"Wie heißen Sie?"} />
                {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
                  <Address path="customer.person.residenceAddress" headline={"Wo wohnen Sie?"} />
                )}
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>Wie können wir Sie erreichen?</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="customer.telephone"
                    label="Ihre Telefonnummer"
                    autoComplete="tel"
                    validators={[
                      requiredValidator("Bitte geben Sie Ihre Telefonnummer an"),
                      {
                        validator: minStringLength,
                        params: [7],
                        message: "Ihre Telefonnummer ist zu kurz",
                      },
                      { validator: isPhoneNumber, message: "Ihre Telefonnummer enthält ungültige Zeichen" },
                    ]}
                  />
                </Grid>
                {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
                  <>
                    <Grid size={12}>
                      <Group
                        type="radio"
                        name="affectedPartyType"
                        question={"Um wen geht es bei Ihrem Anliegen?"}
                        options={[
                          {
                            labelText: "Ich bin selbst betroffen und benötige rechtliche Unterstützung",
                            labelIcon: <PersonIcon />,
                            labelIconActive: <PersonIcon active />,
                            value: "customer",
                          },
                          {
                            labelText: "Eine mir bekannte Person ist betroffen und benötigt rechtliche Unterstützung - zum Beispiel ein Familienmitglied",
                            labelIcon: <FamilyIcon />,
                            labelIconActive: <FamilyIcon active />,
                            value: "coinsured",
                          },
                        ]}
                        validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                      />
                    </Grid>
                    {values.affectedPartyType === "coinsured" && <CoinsuredClient product={product} />}
                  </>
                )}
                {values.caseCategory === "enforceClaim" && <BaseEnforceClaim />}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
